import { inject, Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import { Subject, merge, startWith, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

export interface IBreadcrumb {
  label: string;
  url: string;
  redirectIsBlocked?: boolean;
  replacer?: string;
  customOnClickFn?: () => void;
}

const ROUTE_DATA_BREADCRUMB = 'breadcrumb';

const blockedForRedirectionBreadcrumbs: string[] = [];
const breadcrumbExceptions: string[] = [];
const breadcrumbsHiddenForUrls: string[] = [];

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #renderer: Renderer2;

  readonly breadcrumbs = signal<IBreadcrumb[]>([]);
  readonly url = signal('');

  readonly #replacers = new Map<string, string>();
  readonly #customFunctions = new Map<string, () => void>();
  readonly #updateBreadcrumbsSubject = new Subject<void>();

  public get workspaceMainBreadcrumb(): HTMLElement {
    return document.getElementById('workspace-main-breadcrumb');
  }

  constructor() {
    const rendererFactory = inject(RendererFactory2);
    this.#renderer = rendererFactory.createRenderer(null, null);
    this.#initBreadcrumbsListener();
  }

  public setCurrentPageBreadcrumb(localizableName: string, replacer: string): void {
    this.#replacers.set(replacer, localizableName);
    this.#updateBreadcrumbsSubject.next();
  }

  public setCurrentPageCustomOnClickAction(fn: () => void, replacer: string): void {
    this.#customFunctions.set(replacer, fn);
    this.#updateBreadcrumbsSubject.next();
  }

  public hideWorkspaceBreadcrumb(): void {
    this.#renderer.addClass(this.workspaceMainBreadcrumb, 'hidden');
  }

  public showWorkspaceBreadcrumb(): void {
    this.#renderer.removeClass(this.workspaceMainBreadcrumb, 'hidden');
  }

  #initBreadcrumbsListener(): void {
    merge(this.#router.events, this.#updateBreadcrumbsSubject)
      .pipe(
        startWith(null),
        tap(() => {
          this.url.set(this.#router.url);
          if (!this.#shouldHideBreadcrumbs(this.#router.url)) {
            this.breadcrumbs.set(this.#createBreadcrumbs(this.#route.root, ''));
          } else {
            this.breadcrumbs.set([]);
          }
        })
      )
      .subscribe();
  }

  #createBreadcrumbs(route: ActivatedRoute, parentUrl: string, breadcrumbs: IBreadcrumb[] = []): IBreadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    for (const child of children) {
      if (!child?.snapshot) {
        return [];
      }

      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      let url = parentUrl;

      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data[ROUTE_DATA_BREADCRUMB];
      if (label) {
        const existingBreadcrumb = breadcrumbs.find((breadcrumb: IBreadcrumb) => breadcrumb.url === url);
        const breadcrumbLabel: string = this.#getBreadcrumbLabel(label);

        if (!existingBreadcrumb && !this.#isException(url)) {
          breadcrumbs.push({
            label: breadcrumbLabel,
            url,
            redirectIsBlocked: this.#isBlockedUrl(url),
            replacer: this.#getReplacer(label),
            customOnClickFn: this.#customFunctions.get(breadcrumbLabel)
          });
        }
      }
      this.#createBreadcrumbs(child, url, breadcrumbs);
    }

    if (breadcrumbs[breadcrumbs.length - 1]?.redirectIsBlocked) {
      this.#router.navigate([breadcrumbs[breadcrumbs.length - 2]?.url]);
    }

    return breadcrumbs;
  }

  #getBreadcrumbLabel(label: string): string {
    let finalLabel: string = label;
    if (label?.includes('replacer=')) {
      finalLabel = this.#replacers.get(label);
    }
    return finalLabel;
  }

  #getReplacer(label: string): string {
    return label?.includes('replacer=') ? label : '';
  }

  #isBlockedUrl(url: string): boolean {
    return blockedForRedirectionBreadcrumbs.some(breadcrumb => {
      if (breadcrumb.endsWith('/')) {
        const regex = new RegExp(`^${breadcrumb}\\d+$`);
        return regex.test(url);
      }
      return breadcrumb === url;
    });
  }

  #isException(url: string): boolean {
    return breadcrumbExceptions.some(exception => {
      const regex = new RegExp(`^${exception}(\/\\d+)?$`);
      return regex.test(url);
    });
  }

  #shouldHideBreadcrumbs(url: string): boolean {
    return breadcrumbsHiddenForUrls.some(hiddenUrl => url.includes(hiddenUrl));
  }
}
